import { UNNAMED_CLINIC_LABEL } from '@/dashboard/clinic-context';
import { FinancialDocumentStyles, FinancialDocumentTimestamp } from './ReceiptPrintView';
/**
 * BillDetailCard -- the ONE render of an itemized appointment bill's lines
 * and totals (BILLING-MISSING-DELTA-V1). Render-only: no fetch, no write,
 * no native form control (the conformance guard's new-file law); every
 * number comes off the wire's own derived totals (lib/patient-bills.ts is
 * the ONE derivation home -- this card displays, never re-computes).
 *
 * ONE implementation, three hosts: the front-desk bill panel
 * (BillingEntryPage), the COC read (BillingCompliancePage drill), and the
 * print surface (BillPrintView) all render THIS card, so the two role
 * sheets' shared rows ("Treatment name with include doctor" / "Units" /
 * "Net Amount and Gross Amount" / waive / GST) are served by one component.
 *
 * VOCABULARY: "Gross amount" / "Net amount" are the tracker's own
 * bill-document words. This card never claims an account balance, an
 * amount due, or a paid state -- payment evidence keeps its own homes.
 *
 * THE FULL NAME REACHES THE PAPER (`forPrint`; FRONT-DESK-MONEY-LANE4-V1,
 * the Lane-2 finding "bill print wraps or truncates long treatment names").
 * MEASURED, not assumed: under the V6 switch `[data-visual='v1'] .ac-table`
 * is `table-layout: fixed` and `.ac-table td` is `white-space: nowrap;
 * overflow: hidden; text-overflow: ellipsis` (dashboard.css), so on A4 the
 * five columns take 133px each and the tenant's LONGEST catalogue name --
 * "Full mouth- Implant two piece - all on four" -- needs 260px of the 109px
 * it is given. Three of three lines lost characters; the numbers are in
 * `docs/AGENT-RUNS/front-desk-money-lane4-v1-evidence/print-proof/`.
 *
 * On SCREEN an ellipsis is a density trade-off and the `title` recovers the
 * name on hover. ON PAPER THERE IS NO HOVER: the characters are simply gone
 * from a financial document. So the print host, and only the print host,
 * (a) declares column widths through a `<colgroup>` -- the shipped
 * COLUMN-WIDTHS-V1 mechanism (`ui/DataTable.tsx` `width`), spent here as
 * plain table markup because this card is not a `DataTable` -- and (b) lets
 * the two NAME cells (treatment and doctor) WRAP through the registered
 * `.ac-cell-stack` opt-out, the one class dashboard.css declares for "a cell
 * must opt out of nowrap for its own content only". NO stylesheet is edited:
 * `dashboard.css` is a declared shared file and this lane does not own it.
 *
 * IDENTICAL AT REST: with `forPrint` unset the card emits the markup it
 * emitted before this prop existed, plus a `title` on each of the two name
 * cells -- an attribute, never a pixel. The screen hosts pass nothing.
 */

import {
  BILL_DOCTOR_UNASSIGNED_COPY,
  BILL_PRICE_NOT_SET_COPY,
  BILL_STATUS_LABELS,
  billLineAmountMinorUnits,
  billLineDoctorLabel,
  type PatientBillWireMirror,
} from '@/dashboard/billing/bill-view';
import { formatMinorUnitsInr } from '@/dashboard/billing/billing-entry-view';

export interface BillDetailCardProps {
  readonly bill: PatientBillWireMirror;
  readonly testId?: string;
  readonly waivedLabel?: 'Waived' | 'Waived off';
  /**
   * TRUE only on the PRINT host (`BillPrintView`). Paper has no hover, so
   * the treatment name must arrive whole: the lines table declares column
   * widths and the treatment cell is allowed to wrap. See the header.
   */
  readonly forPrint?: boolean;
  readonly documentLayout?: boolean;
  readonly clinicLabel?: string;
}

/**
 * The print sheet's column allocation, as percentages of the lines table.
 *
 * WHY THESE NUMBERS, AND HOW THEY WERE PICKED. `table-layout: fixed` with no
 * widths gives all five columns an identical 133px share on A4 (table 666px),
 * which starves the two columns carrying names and over-serves three columns
 * of short numerals. Each column's real need was MEASURED on the frame with
 * a clone probe, content only, before the cell's 24px of padding:
 *
 *   TREATMENT 267 | DOCTOR 76 | UNITS 38 (the header, not a cell) |
 *   PRICE 71 | AMOUNT 71
 *
 * The three numeral columns are sized with headroom, because a number must
 * never break across lines and their content is bounded. The two NAME columns
 * are allowed to WRAP, because a name is prose and there is no width at which
 * a catalogue cannot exceed it -- a second line is a complete name, an
 * ellipsis is not.
 *
 * THE SPLIT BETWEEN THE TWO NAME COLUMNS WAS MEASURED, NOT PREFERRED. Four
 * allocations were rendered and their row heights read off the frame, with a
 * treatment name at the catalogue maximum and a doctor name LONGER than any
 * on the seeded roster. 39/17 and 42/15 spill two of three rows to 60px;
 * 36/20 and 34/22 keep all three at the 40px row, none clipped. 36/20 is the
 * one that also leaves the treatment column the larger share.
 */
const PRINT_COL_WIDTHS: readonly string[] = ['36%', '20%', '10%', '17%', '17%'];

/**
 * The printed totals, as ordered rows. Read top to bottom they ARE the
 * product's rule -- gross, then what comes off, then what is added, then the
 * net that is paid. Every amount is the wire's own; this builds no number.
 */
function PRINT_TOTAL_ROWS(totals: PatientBillWireMirror['totals'], documentLayout: boolean): ReadonlyArray<{
  readonly key: string;
  readonly label: string;
  readonly amount: string;
  readonly strong?: boolean;
}> {
  const formatAmount = (value: number) => formatMinorUnitsInr(value, documentLayout ? 2 : 0);
  const rows: Array<{ key: string; label: string; amount: string; strong?: boolean }> = [
    {
      key: 'gross',
      label: 'Gross amount',
      amount: formatAmount(totals.grossMinorUnits),
    },
  ];
  if (totals.waivedMinorUnits > 0) {
    rows.push({
      key: 'waived',
      label: 'Waived off',
      amount: `- ${formatAmount(totals.waivedMinorUnits)}`,
    });
  }
  if (totals.gstMinorUnits !== null) {
    rows.push({
      key: 'gst',
      label: 'GST (recorded)',
      amount: `+ ${formatAmount(totals.gstMinorUnits)}`,
    });
  }
  rows.push({
    key: 'net',
    label: 'Net amount',
    amount: formatAmount(totals.netMinorUnits),
    strong: true,
  });
  if (totals.unpricedCount > 0) {
    rows.push({
      key: 'unpriced',
      label: BILL_PRICE_NOT_SET_COPY,
      amount: `${totals.unpricedCount} line(s) excluded`,
    });
  }
  return rows;
}

export default function BillDetailCard({
  bill,
  testId,
  forPrint = false,
  documentLayout = false,
  clinicLabel,
  waivedLabel = 'Waived off',
}: BillDetailCardProps): JSX.Element {
  const totals = bill.totals;
  const formatAmount = (value: number) => formatMinorUnitsInr(value, documentLayout ? 2 : 0);
  return (
    <div data-testid={testId ?? 'bill-detail-card'} className={documentLayout ? 'payment-receipt-document money-bill-document' : undefined}>
      {documentLayout ? <>
        <FinancialDocumentStyles />
        <small className="financial-document-disclaimer">BILL PREVIEW · NOT A FISCAL INVOICE</small>
        <header className="receipt-letterhead"><div><strong>{clinicLabel ?? UNNAMED_CLINIC_LABEL}</strong><small>Contact unavailable</small></div><div><small>Organisation branding unavailable</small></div><div><small>Address unavailable</small></div></header>
        <h3>ITEMISED BILL PREVIEW <small>{BILL_STATUS_LABELS[bill.status]}</small></h3>
        <div className="receipt-meta"><div><span className="receipt-meta-label">Bill</span><strong>#{bill.id}</strong></div><div><span className="receipt-meta-label">Created · IST</span><strong><FinancialDocumentTimestamp iso={bill.createdAt} /></strong></div><div><span className="receipt-meta-label">Printed / prepared · IST</span><strong><FinancialDocumentTimestamp iso={new Date().toISOString()} /></strong></div></div>
        <dl className="bill-document-identity"><div><dt>Patient</dt><dd>{bill.patientName ?? bill.patientId}</dd></div><div><dt>Appointment</dt><dd>#{bill.appointmentId}</dd></div><div><dt>Finalized · IST</dt><dd>{bill.finalizedAt ? <FinancialDocumentTimestamp iso={bill.finalizedAt} /> : bill.status === 'draft' ? 'Not finalized' : 'Unavailable'}</dd></div><div><dt>Fiscal invoice number</dt><dd>Not established</dd></div></dl>
        {bill.status === 'cancelled' ? <p className="receipt-void">CANCELLED · {bill.cancelledAt ? <FinancialDocumentTimestamp iso={bill.cancelledAt} /> : 'Date unavailable'} · Reason: {bill.cancelReason?.replace(/_/g,' ') ?? 'Not recorded'}. Retained for history.</p> : null}
      </> : null}
      <div className="ac-table-scroll">
        <table
          className={documentLayout ? 'money-bill-document__lines' : 'table ac-table'}
          aria-label={`Bill #${bill.id} lines`}
          data-testid="bill-lines-table"
        >
          {documentLayout ? <caption>Bill lines · performing doctor shown per treatment</caption> : null}
          {documentLayout ? <colgroup data-testid="bill-lines-colgroup"><col style={{width:'6%'}}/><col style={{width:'38%'}}/><col style={{width:'10%'}}/><col style={{width:'23%'}}/><col style={{width:'23%'}}/></colgroup> : forPrint ? (
            <colgroup data-testid="bill-lines-colgroup">
              {PRINT_COL_WIDTHS.map((w, index) => (
                <col key={index} style={{ width: w }} />
              ))}
            </colgroup>
          ) : null}
          <thead>
            <tr>
              {documentLayout ? <><th>No.</th><th>Particulars / performing doctor</th></> : <><th>Treatment</th><th>Doctor</th></>}
              <th className="ac-table__cell--end">Units</th>
              <th className="ac-table__cell--end">Unit price</th>
              <th className="ac-table__cell--end">Amount</th>
            </tr>
          </thead>
          <tbody>
            {bill.items.map((item, index) => {
              const lineAmount = billLineAmountMinorUnits(item);
              const treatmentText = `${item.treatmentName}${
                item.waivedOff ? ` (${waivedLabel.toLowerCase()})` : ''
              }`;
              return (
                <tr
                  key={item.id}
                  data-testid={`bill-line-${item.id}`}
                  className={item.waivedOff ? 'billing-entry__voided-row' : undefined}
                >
                  {/* The name is the one prose cell on the bill. On paper it
                      wraps and arrives whole; on screen it keeps the table's
                      density and the `title` recovers it on hover. */}
                  {documentLayout ? <td>{index + 1}</td> : null}
                  <td title={treatmentText} data-testid={`bill-line-${item.id}-treatment`}>
                    {forPrint || documentLayout ? (
                      <span className="ac-cell-stack">
                        <span>{treatmentText}</span>
                        {documentLayout ? <small data-testid={`bill-line-${item.id}-doctor`}>{billLineDoctorLabel(item)}</small> : null}
                      </span>
                    ) : (
                      treatmentText
                    )}
                  </td>
                  {documentLayout ? null : <td
                    title={billLineDoctorLabel(item)}
                    data-testid={`bill-line-${item.id}-doctor`}
                  >
                    {forPrint ? (
                      <span className="ac-cell-stack">
                        <span>{billLineDoctorLabel(item)}</span>
                      </span>
                    ) : (
                      billLineDoctorLabel(item)
                    )}
                  </td>}
                  <td className="ac-table__cell--end" data-testid={`bill-line-${item.id}-units`}>
                    {item.quantity}
                  </td>
                  <td className="ac-table__cell--end">
                    {item.unitPriceMinorUnits !== null
                      ? formatAmount(item.unitPriceMinorUnits)
                      : BILL_PRICE_NOT_SET_COPY}
                  </td>
                  <td className="ac-table__cell--end" data-testid={`bill-line-${item.id}-amount`}>
                    {lineAmount !== null
                      ? formatAmount(lineAmount)
                      : BILL_PRICE_NOT_SET_COPY}
                  </td>
                </tr>
              );
            })}
            {bill.items.length === 0 ? (
              <tr>
                <td colSpan={5}>No lines on this bill yet.</td>
              </tr>
            ) : null}
          </tbody>
          {forPrint || documentLayout ? (
            /* THE PRINTED TOTALS, AS ARITHMETIC A PATIENT CAN FOLLOW.

               A `<tfoot>` of the LINES table, not a block beside it: the
               amounts then land in the AMOUNT column the reader has already
               been adding down, in the product's own order --
               `net = gross - waived + gst` (lib/patient-bills.ts
               deriveBillTotals). The signs are printed, so no row's effect
               has to be guessed, and NET is the one row set in bold.

               A WAIVER ROW APPEARS ONLY WHEN THERE IS A WAIVER and a GST row
               only when an amount was RECORDED -- an empty row on a bill
               claims a deduction nobody gave.

               Alignment and tabular figures come from `ac-table__cell--end`,
               already carried by the three numeral columns above. Nothing new
               is declared: `dashboard.css` is a shared file this lane does
               not own, and the hierarchy this block still wants (a rule above
               NET, a quieter label column) is written up FOR FACTORY in the
               evidence folder rather than taken. */
            <tfoot data-testid="bill-totals">
              {PRINT_TOTAL_ROWS(totals, documentLayout).map((row) => (
                /* THE NET ROW CARRIES ITS OWN CLASS, and it is not decoration.
                   A positional selector cannot name it: when a bill has an
                   unpriced line the LAST row is the "Price not set" warning,
                   not the net, so `tr:last-of-type` would weight the warning
                   and leave the number the patient pays unmarked. The class
                   names the row by what it IS. Unused at rest -- no
                   stylesheet declares it yet; the rules are proposed FOR
                   FACTORY in the evidence folder. */
                <tr
                  key={row.key}
                  className={row.key === 'net' ? 'ac-bill-total--net' : undefined}
                  data-testid={`bill-total-row-${row.key}`}
                >
                  <td colSpan={4} className="ac-table__cell--end">
                    {row.strong ? <strong>{row.label}</strong> : row.label}
                  </td>
                  <td
                    className="ac-table__cell--end"
                    data-testid={`bill-total-${row.key}`}
                  >
                    {row.strong ? <strong>{row.amount}</strong> : row.amount}
                  </td>
                </tr>
              ))}
            </tfoot>
          ) : null}
        </table>
      </div>
      {/* THE SCREEN TOTALS, UNCHANGED. The print host renders its totals
          in the lines table's own tfoot above (the amounts must land in
          the AMOUNT column on paper); rendering both would print the
          totals twice, which the first frame of this package did. The
          screen keeps the block it has always had, byte for byte. */}
      {forPrint || documentLayout ? null : (
        <dl className="billing-entry__receipt-facts" data-testid="bill-totals">
          <div>
            <dt>Gross amount</dt>
            <dd data-testid="bill-total-gross">
              {formatAmount(totals.grossMinorUnits)}
            </dd>
          </div>
          {totals.waivedMinorUnits > 0 ? (
            <div>
              <dt>{waivedLabel}</dt>
              <dd data-testid="bill-total-waived">
                - {formatAmount(totals.waivedMinorUnits)}
              </dd>
            </div>
          ) : null}
          {totals.gstMinorUnits !== null ? (
            <div>
              <dt>GST (recorded)</dt>
              <dd data-testid="bill-total-gst">
                {formatAmount(totals.gstMinorUnits)}
              </dd>
            </div>
          ) : null}
          <div>
            <dt>Net amount</dt>
            <dd data-testid="bill-total-net">
              <strong>{formatAmount(totals.netMinorUnits)}</strong>
            </dd>
          </div>
          {bill.status==='finalized'?<>
            <div><dt>Applied payments</dt><dd>{bill.balance?.state==='known'?formatAmount(bill.balance.paidMinorUnits!):'Allocation unknown'}</dd></div>
            <div><dt>Bill balance</dt><dd>{bill.balance?.state==='known'?formatAmount(bill.balance.dueMinorUnits!):bill.balance?.state==='allocation_unknown'?'Reconcile earlier payments':'Unavailable'}</dd></div>
          </>:null}
          {totals.unpricedCount > 0 ? (
            <div>
              <dt>{BILL_PRICE_NOT_SET_COPY}</dt>
              <dd data-testid="bill-total-unpriced">
                {totals.unpricedCount} line(s) excluded from these totals
              </dd>
            </div>
          ) : null}
        </dl>
      )}
      {documentLayout ? <>
        <p className="financial-document-gap">{totals.gstMinorUnits === null ? 'GST not recorded. ' : ''}Consultant not supplied. Created by: Not recorded. Signature details not supplied.</p>
        {bill.status === 'finalized' ? <dl className="bill-document-settlement"><div><dt>Payments applied</dt><dd>{bill.balance?.state === 'known' ? formatAmount(bill.balance.paidMinorUnits!) : 'Allocation unknown'}</dd></div><div><dt>Amount due</dt><dd>{bill.balance?.state === 'known' ? formatAmount(bill.balance.dueMinorUnits!) : 'Unavailable'}</dd></div></dl> : null}
        <footer className="financial-document-foot">Paid amounts use recorded bill allocations only. Printing never records payment.</footer>
      </> : null}
      {/* THE STATUS LINE IS APPLICATION CHROME, NOT DOCUMENT CONTENT.
          On SCREEN it orients the operator: which bill, what state, whose,
          and -- when a line has no recorded doctor -- what the `Unassigned`
          cell above means. NONE of that belongs on a printed bill a patient
          keeps. "lines without a doctor show Unassigned" is implementation
          commentary; the identity it repeats (bill number, status, patient,
          appointment) is ALREADY in the print sheet's own header subs
          (`BillPrintView.tsx` `subs`), so printing it here also says
          everything twice.

          Removed from the printed output, kept verbatim on screen. */}
      {forPrint || documentLayout ? null : (
        <p className="dashboard-page__note" data-testid="bill-status-line">
          Bill #{bill.id} - {BILL_STATUS_LABELS[bill.status]}
          {bill.status === 'cancelled' && bill.cancelReason !== null
            ? ` (${bill.cancelReason.replace(/_/g, ' ')})`
            : ''}
          {' - '}
          {bill.patientName ?? 'Patient record'} - Appointment #
          {bill.appointmentId}
          {bill.items.some((i) => i.performingDoctorId === null) &&
          bill.items.length > 0
            ? ` - lines without a doctor show ${BILL_DOCTOR_UNASSIGNED_COPY}`
            : ''}
        </p>
      )}
    </div>
  );
}
